import { useState, useEffect } from "react";
import API from "@/lib/api";
import { getErrorMessage } from "@/lib/errorHandler";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Shield, Trash2, Edit, Key, Search, UserCog, Building2, MapPin } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { useAuth } from "@/contexts/AuthContext";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useCampusContext } from "@/contexts/CampusContext";

// Must stay in sync with ORG_ADMIN_DEFAULT_PERMISSIONS in admin_management.py
const ORG_ADMIN_DEFAULT_PERMISSIONS = {
  can_view_reports: true,
  can_view_analytics: true,
  can_manage_report_configs: true,
  can_manage_quiz_takers: true,
  can_delete_quizzes: true,
  can_manage_categories: true,
  can_manage_sequences: true,
  // can_manage_courses: disabled by default — must be explicitly granted
};

const ALL_PERMISSION_LABELS = {
  can_view_quizzes: "View Quizzes",
  can_create_quizzes: "Create Quizzes",
  can_edit_quizzes: "Edit Quizzes",
  can_delete_quizzes: "Delete Quizzes",
  can_manage_sequences: "Manage Sequences",
  can_manage_courses: "Manage Courses",
  can_view_reports: "View Reports",
  can_export_reports: "Export Reports",
  can_view_analytics: "View Analytics",
  can_manage_categories: "Manage Categories",
  can_manage_groups: "Manage Groups",
  can_manage_quiz_takers: "Manage Quiz Takers",
  can_manage_report_configs: "Manage Report Configs",
  can_view_system_stats: "View System Stats",
  can_unlock_accounts: "Unlock Accounts",
};

export default function AdminManagement() {
  const { user: currentUser } = useAuth();
  const { selectedOrg, isSuperadmin } = useOrganizationContext();
  const { selectedCampus, isOrgAdmin, isMultiCampusAdmin, shouldShowSelector } = useCampusContext();

  // Only show/grant permissions the current user themselves holds.
  // Superadmin can grant everything.
  const grantablePermissions = isSuperadmin
    ? ALL_PERMISSION_LABELS
    : Object.fromEntries(
        Object.entries(ALL_PERMISSION_LABELS).filter(([key]) => currentUser?.permissions?.[key])
      );
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);
  const [stats, setStats] = useState({ total_admins: 0, total_superadmins: 0, locked_admins: 0 });
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [campusList, setCampusList] = useState([]);

  // isSuperadmin is now provided by useOrganizationContext

  // Form state — permissions are populated by resetForm() which scopes to what currentUser can grant
  const [formData, setFormData] = useState({
    email: "",
    name: "",
    role: "admin",
    organization_id: "",
    campus_ids: [],
    permissions: {}
  });

  useEffect(() => {
    loadAdmins();
    loadStats();
    if (isSuperadmin) {
      loadOrganizations();
    }
    // Load campuses (needed to display campus names in the table for all roles)
    loadCampusList();
  }, [page, search, isSuperadmin, isOrgAdmin, selectedOrg, selectedCampus?.id]); // Re-load when org or campus changes

  const loadOrganizations = async () => {
    setLoadingOrgs(true);
    try {
      const res = await API.get("/admin-management/organizations");
      setOrganizations(res.data);
    } catch (err) {
      console.error("Failed to load organizations:", err);
      toast.error("Failed to load organizations");
    }
    setLoadingOrgs(false);
  };

  const loadCampusList = async () => {
    try {
      const params = {};
      if (isSuperadmin && selectedOrg?.id) params.organization_id = selectedOrg.id;
      const res = await API.get("/campuses", { params });
      setCampusList(res.data || []);
    } catch (err) {
      console.error("Failed to load campuses:", err);
    }
  };

  const loadAdmins = async () => {
    setLoading(true);
    try {
      const params = { page, limit: 20 };
      if (search) params.search = search;

      // If superadmin and organization is selected, filter admins by org
      if (isSuperadmin && selectedOrg?.id) {
        params.organization_id = selectedOrg.id;
      }
      if ((isOrgAdmin || isMultiCampusAdmin) && selectedCampus?.id) {
        params.campus_id = selectedCampus.id;
      }

      const res = await API.get("/admin-management/admins", { params });
      setAdmins(res.data.data);
      setTotal(res.data.total);
    } catch (err) {
      toast.error("Failed to load admins");
    }
    setLoading(false);
  };

  const loadStats = async () => {
    try {
      const params = {};
      // If superadmin and organization is selected, filter stats by org
      if (isSuperadmin && selectedOrg?.id) {
        params.organization_id = selectedOrg.id;
      }
      if (isOrgAdmin && selectedCampus?.id) {
        params.campus_id = selectedCampus.id;
      }
      const res = await API.get("/admin-management/admin-stats", { params });
      setStats(res.data);
    } catch (err) {
      console.error("Failed to load stats:", err);
    }
  };

  const handleAdd = async () => {
    if (!formData.email || !formData.name) {
      toast.error("Email and name are required");
      return;
    }

    if (isSuperadmin && !formData.organization_id) {
      toast.error("Please select an organization");
      return;
    }

    try {
      const payload = {
        email: formData.email,
        name: formData.name,
        role: formData.role || "admin",
        permissions: formData.permissions,
        campus_ids: formData.campus_ids,
      };
      if (isSuperadmin) payload.organization_id = formData.organization_id;

      await API.post("/admin-management/admins", payload);
      toast.success("Admin created successfully");
      setShowAddDialog(false);
      resetForm();
      loadAdmins();
      loadStats();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to create admin"));
    }
  };

  const handleEdit = async () => {
    if (!selectedAdmin) return;

    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        permissions: formData.permissions,
        campus_ids: formData.campus_ids,
      };
      if (isSuperadmin && formData.role) payload.role = formData.role;
      await API.put(`/admin-management/admins/${selectedAdmin.id}`, payload);
      toast.success("Admin updated successfully");
      setShowEditDialog(false);
      setSelectedAdmin(null);
      resetForm();
      loadAdmins();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to update admin"));
    }
  };

  const handleDelete = async (admin) => {
    const confirmMessage = `Delete ${admin.role} "${admin.name}"?\n\nThis will permanently delete:\n- All their created quizzes, categories, and groups\n- All submissions to their quizzes\n\nThis action cannot be undone.`;

    if (!window.confirm(confirmMessage)) return;

    try {
      await API.delete(`/admin-management/admins/${admin.id}`);
      toast.success("Admin deleted successfully");
      loadAdmins();
      loadStats();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to delete admin"));
    }
  };

  const handleResetOTP = async (admin) => {
    if (!window.confirm(`Reset OTP attempts for "${admin.name}"?`)) return;

    try {
      await API.post(`/admin-management/admins/${admin.id}/reset-otp`);
      toast.success("OTP attempts reset successfully");
      loadAdmins();
    } catch (err) {
      toast.error(getErrorMessage(err, "Failed to reset OTP"));
    }
  };

  const openEditDialog = (admin) => {
    setSelectedAdmin(admin);
    // Normalize permissions: every known key must be explicitly true or false.
    // Without this, missing keys are omitted from the save payload, the DB never
    // stores an explicit false, and build_user_permissions defaults kick in on login.
    const normalizedPermissions = Object.fromEntries(
      Object.keys(ALL_PERMISSION_LABELS).map(key => [key, admin.permissions?.[key] === true])
    );
    setFormData({
      email: admin.email,
      name: admin.name,
      role: admin.role || "admin",
      campus_ids: admin.campus_ids || (admin.campus_id ? [admin.campus_id] : []),
      permissions: normalizedPermissions
    });
    setShowEditDialog(true);
  };

  const resetForm = () => {
    // All known permissions default to false. The creator can only enable the ones
    // they themselves hold (enforced by rendering only grantablePermissions checkboxes),
    // but ALL keys are included in the payload so the backend stores explicit false
    // values — preventing build_user_permissions defaults from overriding them.
    const defaultPermissions = Object.fromEntries(
      Object.keys(ALL_PERMISSION_LABELS).map(key => [key, false])
    );
    setFormData({
      email: "",
      name: "",
      role: "admin",
      organization_id: "",
      campus_ids: [],
      permissions: defaultPermissions
    });
  };

  const togglePermission = (key) => {
    setFormData({
      ...formData,
      permissions: {
        ...formData.permissions,
        [key]: !formData.permissions[key]
      }
    });
  };

  // When role changes in the Add form, pre-populate permissions appropriate for that role
  const handleRoleChange = (role) => {
    const base = Object.fromEntries(Object.keys(ALL_PERMISSION_LABELS).map(k => [k, false]));
    const permissions = role === 'org_admin'
      ? { ...base, ...ORG_ADMIN_DEFAULT_PERMISSIONS }
      : base;
    setFormData(prev => ({ ...prev, role, permissions }));
  };


  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Shield className="w-8 h-8" />
            Admin Management
          </h1>
          <p className="text-gray-600 mt-1">
            Manage admin users and permissions
            {isSuperadmin && selectedOrg && (
              <span className="ml-2 text-sm text-blue-600">
                • Viewing: <span className="font-semibold">{selectedOrg.name}</span> admins only
              </span>
            )}
            {isSuperadmin && !selectedOrg && (
              <span className="ml-2 text-sm text-amber-600">
                • Viewing: <span className="font-semibold">All Admins + Superadmins</span>
              </span>
            )}
          </p>
        </div>
        <Button onClick={() => { resetForm(); setShowAddDialog(true); }}>
          <Plus className="w-4 h-4 mr-2" />
          Add Admin
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Total Admins</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold">{stats.total_admins}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Superadmins</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-purple-600">{stats.total_superadmins}</div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-gray-600">Locked Admins</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">{stats.locked_admins}</div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <Card className="mb-6">
        <CardContent className="pt-6">
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search by name or email..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
              />
            </div>
            <Button variant="outline" onClick={() => setSearch("")}>Clear</Button>
          </div>
        </CardContent>
      </Card>

      {/* Admin List */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left p-4 font-medium">Name</th>
                  <th className="text-left p-4 font-medium">Email</th>
                  <th className="text-left p-4 font-medium">Role</th>
                  <th className="text-left p-4 font-medium">Organization</th>
                  <th className="text-left p-4 font-medium">Permissions</th>
                  <th className="text-left p-4 font-medium">Status</th>
                  <th className="text-right p-4 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {loading ? (
                  <>
                    <style>{`@keyframes am-shimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}`}</style>
                    {[...Array(5)].map((_, i) => (
                      <tr key={i} style={{borderBottom:'1px solid #f1f5f9'}}>
                        <td className="p-4"><div style={{height:13,width:110,borderRadius:6,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'am-shimmer 1.4s ease infinite'}} /></td>
                        <td className="p-4"><div style={{height:13,width:150,borderRadius:6,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'am-shimmer 1.4s ease infinite'}} /></td>
                        <td className="p-4"><div style={{height:20,width:60,borderRadius:10,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'am-shimmer 1.4s ease infinite'}} /></td>
                        <td className="p-4"><div style={{height:13,width:100,borderRadius:6,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'am-shimmer 1.4s ease infinite'}} /></td>
                        <td className="p-4"><div style={{height:13,width:130,borderRadius:6,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'am-shimmer 1.4s ease infinite'}} /></td>
                        <td className="p-4"><div style={{height:20,width:70,borderRadius:10,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'am-shimmer 1.4s ease infinite'}} /></td>
                        <td className="p-4 text-right"><div style={{height:28,width:60,borderRadius:8,background:'linear-gradient(90deg,#e2e8f0 25%,#f1f5f9 50%,#e2e8f0 75%)',backgroundSize:'200% 100%',animation:'am-shimmer 1.4s ease infinite',marginLeft:'auto'}} /></td>
                      </tr>
                    ))}
                  </>
                ) : admins.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="py-16 text-center">
                      <svg width="72" height="64" viewBox="0 0 72 64" fill="none" className="mx-auto mb-4 opacity-70">
                        <circle cx="36" cy="20" r="11" fill="#EEF2FF" stroke="#C7D2FE" strokeWidth="1.5"/>
                        <circle cx="36" cy="20" r="5.5" fill="#A5B4FC"/>
                        <path d="M15 52c2-9 10-15 21-15s19 6 21 15" stroke="#C7D2FE" strokeWidth="2" strokeLinecap="round" fill="none"/>
                        <circle cx="52" cy="44" r="10" fill="#4F46E5"/>
                        <path d="M48 44l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                      <p className="text-slate-600 font-semibold text-sm">No admins found</p>
                      <p className="text-slate-400 text-xs mt-1">Add an admin to get started</p>
                    </td>
                  </tr>
                ) : admins.map((admin) => (
                    <tr key={admin.id} className="hover:bg-gray-50">
                      <td className="p-4">
                        <div>
                          <div className="font-medium text-sm">{admin.name}</div>
                          {admin.campuses && admin.campuses.length > 0 ? (
                            <div className="flex flex-wrap gap-1 mt-1">
                              {admin.campuses.map(c => (
                                <div key={c.id} className="flex items-center gap-1">
                                  <MapPin className="w-3 h-3 text-blue-400 flex-shrink-0" />
                                  <span className="text-xs text-blue-600 font-medium">{c.name}</span>
                                </div>
                              ))}
                            </div>
                          ) : admin.role === 'org_admin' ? (
                            <span className="text-xs text-indigo-500 mt-1 block">All campuses</span>
                          ) : null}
                        </div>
                      </td>
                      <td className="p-4">{admin.email}</td>
                      <td className="p-4">
                        <Badge variant={admin.role === 'superadmin' ? 'default' : admin.role === 'org_admin' ? 'outline' : 'secondary'}>
                          {admin.role === 'superadmin' ? 'Superadmin' : admin.role === 'org_admin' ? 'Org Admin' : 'Admin'}
                        </Badge>
                      </td>
                      <td className="p-4">
                        {admin.organization ? (
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-gray-400" />
                            <div>
                              <div className="text-sm font-medium">{admin.organization.name}</div>
                              <div className="text-xs text-gray-500">{admin.organization.plan}</div>
                            </div>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">No organization</span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="text-sm text-gray-600">
                          {Object.values(admin.permissions || {}).filter(Boolean).length} permissions
                        </div>
                      </td>
                      <td className="p-4">
                        {admin.locked_until ? (
                          <Badge variant="destructive">Locked</Badge>
                        ) : admin.failed_otp_attempts > 0 ? (
                          <Badge variant="outline">{admin.failed_otp_attempts} failed attempts</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">Active</Badge>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex justify-end gap-2">
                          {admin.role !== 'superadmin' && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => openEditDialog(admin)}
                              >
                                <Edit className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleResetOTP(admin)}
                                title="Reset OTP"
                              >
                                <Key className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => handleDelete(admin)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {total > 20 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button
            variant="outline"
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
          >
            Previous
          </Button>
          <span className="px-4 py-2">Page {page} of {Math.ceil(total / 20)}</span>
          <Button
            variant="outline"
            onClick={() => setPage(p => p + 1)}
            disabled={page >= Math.ceil(total / 20)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Add Admin Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5" />
              Add New Admin
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                placeholder="admin@example.com"
              />
            </div>

            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Admin Name"
              />
            </div>

            {isSuperadmin && (
              <div>
                <Label>Organization *</Label>
                <Select
                  value={formData.organization_id}
                  onValueChange={(value) => setFormData({ ...formData, organization_id: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select organization" />
                  </SelectTrigger>
                  <SelectContent>
                    {loadingOrgs ? (
                      <SelectItem value="" disabled>Loading organizations...</SelectItem>
                    ) : organizations.length === 0 ? (
                      <SelectItem value="" disabled>No organizations available</SelectItem>
                    ) : (
                      organizations.map((org) => (
                        <SelectItem key={org.id} value={org.id}>
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            <span>{org.name}</span>
                            <Badge variant="outline" className="ml-2">{org.plan}</Badge>
                          </div>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  Select which organization this admin will manage
                </p>
              </div>
            )}

            {!isSuperadmin && currentUser?.organization && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-center gap-2 text-sm text-blue-900">
                  <Building2 className="w-4 h-4" />
                  <span className="font-medium">Creating admin for:</span>
                  <span>{currentUser.organization.name}</span>
                  <Badge variant="outline">{currentUser.organization.plan}</Badge>
                </div>
              </div>
            )}

            {/* Role selection — superadmin can assign org_admin */}
            {(isSuperadmin || isOrgAdmin) && (
              <div>
                <Label>Role</Label>
                <Select
                  value={formData.role}
                  onValueChange={handleRoleChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin (campus-scoped)</SelectItem>
                    {isSuperadmin && (
                      <SelectItem value="org_admin">Org Admin (all campuses)</SelectItem>
                    )}
                  </SelectContent>
                </Select>
                <p className="text-xs text-gray-500 mt-1">
                  {formData.role === "org_admin" ? "Org Admin can see all campuses and manage admins." : "Admin is scoped to a single campus."}
                </p>
              </div>
            )}

            {/* Campus selection — shown for admin role when campuses are available */}
            {campusList.length > 0 && formData.role !== "org_admin" && (
              <div>
                <Label className="mb-2 block">
                  Campus Assignment
                  <span className="text-xs text-gray-500 font-normal ml-2">(select one or more)</span>
                </Label>
                <div className="border rounded-lg p-3 bg-gray-50 space-y-2 max-h-48 overflow-y-auto">
                  {campusList.map((c) => (
                    <div key={c.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`campus-${c.id}`}
                        checked={formData.campus_ids.includes(c.id)}
                        onCheckedChange={(checked) => {
                          setFormData(prev => ({
                            ...prev,
                            campus_ids: checked
                              ? [...prev.campus_ids, c.id]
                              : prev.campus_ids.filter(id => id !== c.id)
                          }));
                        }}
                      />
                      <label htmlFor={`campus-${c.id}`} className="text-sm cursor-pointer flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-slate-400" />
                        {c.name}
                      </label>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Admin will only see data from their assigned campuses.
                </p>
              </div>
            )}

            <div>
              <Label className="mb-3 block">Permissions</Label>
              <div className="grid grid-cols-2 gap-3 border rounded-lg p-4 bg-gray-50">
                {Object.entries(grantablePermissions).map(([key, label]) => (
                  <div key={key} className="flex items-center space-x-2">
                    <Checkbox
                      id={key}
                      checked={formData.permissions[key]}
                      onCheckedChange={() => togglePermission(key)}
                    />
                    <label htmlFor={key} className="text-sm cursor-pointer">
                      {label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAdd}>
              Create Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Admin Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="w-5 h-5" />
              Edit Admin
            </DialogTitle>
          </DialogHeader>

          {/* Admin identity summary */}
          {selectedAdmin && (
            <div className="flex items-center gap-3 px-1 py-2 bg-slate-50 rounded-lg border border-slate-200">
              <div className="w-9 h-9 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-sm font-semibold flex-shrink-0">
                {selectedAdmin.name?.charAt(0)?.toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium text-slate-900 truncate">{selectedAdmin.name}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  {selectedAdmin.campuses && selectedAdmin.campuses.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {selectedAdmin.campuses.map(c => (
                        <div key={c.id} className="flex items-center gap-1">
                          <MapPin className="w-3 h-3 text-blue-400 flex-shrink-0" />
                          <span className="text-xs text-blue-600 font-medium">{c.name}</span>
                        </div>
                      ))}
                    </div>
                  ) : selectedAdmin.role === 'org_admin' ? (
                    <div className="flex items-center gap-1">
                      <MapPin className="w-3 h-3 text-indigo-400 flex-shrink-0" />
                      <span className="text-xs text-indigo-500">All campuses</span>
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">No campus assigned</span>
                  )}
                  <span className="text-xs text-gray-300">·</span>
                  <span className="text-xs text-gray-500">{selectedAdmin.email}</span>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <div>
              <Label>Email *</Label>
              <Input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              />
            </div>

            <div>
              <Label>Name *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              />
            </div>

            {/* Role selection in edit — superadmin can change role */}
            {isSuperadmin && selectedAdmin?.role !== 'superadmin' && (
              <div>
                <Label>Role</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin (campus-scoped)</SelectItem>
                    <SelectItem value="org_admin">Org Admin (all campuses)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Campus selection in edit — multi-select checkboxes */}
            {campusList.length > 0 && formData.role !== "org_admin" && (
              <div>
                <Label className="mb-2 block">
                  Campus Assignment
                  <span className="text-xs text-gray-500 font-normal ml-2">(select one or more)</span>
                </Label>
                <div className="border rounded-lg p-3 bg-gray-50 space-y-2 max-h-48 overflow-y-auto">
                  {campusList.map((c) => (
                    <div key={c.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`edit-campus-${c.id}`}
                        checked={formData.campus_ids.includes(c.id)}
                        onCheckedChange={(checked) => {
                          setFormData(prev => ({
                            ...prev,
                            campus_ids: checked
                              ? [...prev.campus_ids, c.id]
                              : prev.campus_ids.filter(id => id !== c.id)
                          }));
                        }}
                      />
                      <label htmlFor={`edit-campus-${c.id}`} className="text-sm cursor-pointer flex items-center gap-1">
                        <MapPin className="w-3 h-3 text-slate-400" />
                        {c.name}
                      </label>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Admin will only see data from their assigned campuses.
                </p>
              </div>
            )}

            <div>
              <Label className="mb-3 block">Permissions</Label>
              <div className="grid grid-cols-2 gap-3 border rounded-lg p-4 bg-gray-50">
                {Object.entries(grantablePermissions).map(([key, label]) => (
                  <div key={key} className="flex items-center space-x-2">
                    <Checkbox
                      id={`edit-${key}`}
                      checked={formData.permissions[key]}
                      onCheckedChange={() => togglePermission(key)}
                    />
                    <label htmlFor={`edit-${key}`} className="text-sm cursor-pointer">
                      {label}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleEdit}>
              Update Admin
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
